import { z } from "zod";

import { ProfileExitSchema } from "./profiles.js";

// ── Profile Cleaning: scoped purge of dead profiles ──

// A "dead" profile is one whose enrichment failed permanently (`enrichmentStatus`
// is set, e.g. "not_found"): its LinkedIn URL is broken, so it can never be
// enriched. The admin Profile Cleaning dashboard lists them and offers two
// actions: purge everything (`DELETE /dead-profiles`) or purge the current
// selection (`POST /dead-profiles/delete`, this schema).
//
// Why POST and not a body on DELETE: the frontend api client exposes
// `delete: <T>(path: string)`: path only, no body. `POST /profiles/deactivate`,
// `POST /profiles/restore` and `POST /profiles/by-ids` are the established
// bulk-ids precedents in this codebase, so the scoped purge follows them.
//
// The 2000 cap is the same order of magnitude as `POST /profiles/by-ids`'s 5000.
// It was chosen when only "Clean selected" called this route, where one page of
// the cleaning table holds 50 rows and the cap was unreachable. "Clean all" now
// calls it too, with every row on screen, so the cap IS reachable: the frontend
// splits the run into batches of this size, which is why the number is exported
// rather than inlined: two copies of it would drift, and the drift would surface
// as a 400 on the largest cleans only.
export const DELETE_DEAD_PROFILES_MAX_IDS = 2000;

export const DeleteDeadProfilesRequestSchema = z.object({
    profile_ids: z.array(z.uuid()).min(1).max(DELETE_DEAD_PROFILES_MAX_IDS),
});
export type DeleteDeadProfilesRequest = z.infer<typeof DeleteDeadProfilesRequestSchema>;

// `skipped_count` reports how many of the requested ids were no longer deletable
// at deletion time: already purged by a concurrent admin, or re-enriched
// successfully in the meantime so `enrichmentStatus` went back to null and the
// profile is alive again. The endpoint re-queries the dead set instead of
// trusting the list the frontend was showing, so a stale selection is normal
// rather than exceptional: those ids are reported, never silently dropped, and
// never turned into an error or a 404.
export const DeleteDeadProfilesResponseSchema = z.object({
    deleted_count: z.number().int(),
    updated_pipelines: z.number().int(),
    skipped_count: z.number().int(),
});
export type DeleteDeadProfilesResponse = z.infer<typeof DeleteDeadProfilesResponseSchema>;

// ── Profile Cleaning: last clean (#619) ──

// When profiles were last cleaned, for the Cleaning actions band: the latest row
// of `profile_clean_runs`, which the purge and the merge write when they change
// something. `null` until the first clean; nothing recorded one before #619.
//
// `ran_by_name` is null on its own when the admin's account has been deleted
// since (the FK is SET NULL): the clean still happened, only its author is gone.
export const LastCleanSchema = z.object({
    ran_at: z.string(),
    ran_by_name: z.string().nullable(),
});
export type LastClean = z.infer<typeof LastCleanSchema>;

export const LastCleanResponseSchema = z.object({
    last_clean: LastCleanSchema.nullable(),
});
export type LastCleanResponse = z.infer<typeof LastCleanResponseSchema>;

// ── Profile Cleaning: exits in mixed currencies (#709) ──

// A profile whose exit amounts are not all in one currency: their sum cannot be
// read as one figure, and no conversion exists, so an administrator converts
// them by hand, from the row's "Convert currencies" dialog (#732) or by
// re-importing the exits with an overwrite. Never part of a clean: nothing is
// deleted or merged. `currencies` lists each currency once, `null` for the
// amounts the file gave without one.
export const MixedCurrencyExitProfileSchema = z.object({
    id: z.string(),
    full_name: z.string(),
    linkedin_url: z.string(),
    currencies: z.array(z.string().nullable()),
    /** Locked from cleaning (#702): shown with its padlock like every other row. */
    is_cleaning_locked: z.boolean(),
    /** When the latest of its exits was written. */
    updated_at: z.string(),
    /**
     * Every exit of the profile, most recent first (no date last), the ones
     * without an amount included: the conversion dialog lists them all (#732).
     */
    exits: z.array(ProfileExitSchema),
});
export type MixedCurrencyExitProfile = z.infer<typeof MixedCurrencyExitProfileSchema>;

export const MixedCurrencyExitProfilesResponseSchema = z.object({
    data: z.array(MixedCurrencyExitProfileSchema),
    total: z.number().int(),
});
export type MixedCurrencyExitProfilesResponse = z.infer<typeof MixedCurrencyExitProfilesResponseSchema>;

// The converted amounts of a profile's exits, all written in `currency` at once
// (#732). Every exit that has an amount must be listed; the ones without an
// amount are left as they are. The rules (known currency, positive amounts,
// every amount converted) are checked by the route, not by the schema.
export const ConvertExitCurrenciesRequestSchema = z.object({
    currency: z.string(),
    exits: z.array(
        z.object({
            id: z.string(),
            exit_amount: z.number(),
        }),
    ),
});
export type ConvertExitCurrenciesRequest = z.infer<typeof ConvertExitCurrenciesRequestSchema>;

export const ConvertExitCurrenciesResponseSchema = z.object({
    converted_count: z.number().int(),
});
export type ConvertExitCurrenciesResponse = z.infer<typeof ConvertExitCurrenciesResponseSchema>;

// ── Profile Cleaning: exits & investments to complete (#754) ──

// An active Business Angel or Exit entrepreneur with a career on file whose
// "Exits & investments" tab is empty (no exit, no investor title) and that no
// Invyo expert has verified: an expert completes the tab, or verifies the
// profile when nothing relevant can be found. Read only: never cleaned, and out
// of "All issues" and of the sidebar badge.

/** The categories that put a profile in the queue, in the order the Details column reads them. */
export const EXITS_INVESTMENTS_TO_COMPLETE_CATEGORIES = ["business_angels", "exit_entrepreneurs"] as const;

export const ExitsInvestmentsToCompleteProfileSchema = z.object({
    id: z.string(),
    full_name: z.string(),
    linkedin_url: z.string(),
    /** Which of the two categories the profile's Invyo types belong to: one or both. */
    categories: z.array(z.enum(EXITS_INVESTMENTS_TO_COMPLETE_CATEGORIES)),
    /** The last LinkedIn refresh; null for a profile never refreshed since its import. */
    data_updated_at: z.string().nullable(),
});
export type ExitsInvestmentsToCompleteProfile = z.infer<typeof ExitsInvestmentsToCompleteProfileSchema>;

export const ExitsInvestmentsToCompleteResponseSchema = z.object({
    data: z.array(ExitsInvestmentsToCompleteProfileSchema),
    total: z.number().int(),
});
export type ExitsInvestmentsToCompleteResponse = z.infer<typeof ExitsInvestmentsToCompleteResponseSchema>;

// ── Profile Cleaning: sidebar badge (#753) ──

// The "All issues" figure of the page, unfiltered, behind the "Profile Cleaning"
// sidebar badge: broken-URL profiles (deactivated ones included), every active
// member of a duplicate group (canonical included) and every active profile with
// exits in mixed currencies. A profile in two of them counts twice, like the page.
// `exits_investments_to_complete` (#754) is the size of the review-only queue of
// the same name, named apart in the badge's tooltip: never part of `count`. Null
// when it could not be counted and never was: the badge is served without it.
export const IssueCountResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        count: z.number().int(),
        exits_investments_to_complete: z.number().int().nullable(),
    }),
});
export type IssueCountResponse = z.infer<typeof IssueCountResponseSchema>;
